import {observer} from "mobx-react";
import styled, {css} from "styled-components";
import {useServices} from "../../../hooks/use-services.hook";
import {BookingSearchResultsCardsViewComponent} from "./cards-view/booking-search-results-cards-view.component";
import {
    BookingSearchResultViewMode
} from "../../../services/agency-booking-search/agency-booking-search.service.interface";
import {BookingSearchResultsTableViewComponent} from "./table-view/booking-search-results-table-view.component";
import React from "react";
import {BookingSearchResultsHeaderComponent} from "./booking-search-results-header.component";
import {
    IBookingSearchResultRecordViewModel
} from "../../../services/agency-booking-search/booking-search-result-record.view-model.interface";
import {ChevronForwardIcon} from "../../../assets/svg/chevron-forward.icon";
import {SpinnerComponent} from "../../../components/spinner/spinner.component";

const ContainerBox = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;
    gap: ${props => props.theme.spacing.spacing12};
`

const ResultsBox = styled.div`
    overflow: auto;
    flex-grow: 1;
`

const NoResultsBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
`

const PageNavigationBox = styled.div`
    display: flex;
    flex-direction: row;    
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.flexGap12};
    width: 100%;
    font-size: ${props => props.theme.fontSize.body2};
    font-weight: ${props => props.theme.fontWeight.semiBold};
`

const PageNumberBox = styled.div<{$isCurrentPage: boolean}>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${props => props.theme.spacing.spacing12};
    border-radius: 50%;
    width: 32px;
    height: 32px;
    
    
    ${props => props.$isCurrentPage 
            ? css`
                background-color: ${props => props.theme.colors.primary};
                color: ${props => props.theme.colors.primaryContrast};
            `
            : css`
                background-color: ${props => props.theme.colors.lightShade};
                color: ${props => props.theme.colors.lightContrast};
                cursor: pointer;
                &:hover {
                    background-color: ${props => props.theme.colors.medium};
                    color: ${props => props.theme.colors.mediumContrast};
                }
            `
    }
`

const NextPageButtonBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 4px;
    color: ${props => props.theme.colors.medium};
    cursor: pointer;
    &:hover {
        color: ${props => props.theme.colors.dark};
    }
`

interface PageNavigatorComponentProps {
    pagesCount: number;
    currentPageIndex: number;
    hasMorePages: boolean;
    onPageClick: (pageIndex: number) => void;
    onNextPageClick: () => void;
}

const PageNavigationComponent: React.FC<PageNavigatorComponentProps> = observer((props) => {
    const services = useServices();
    if(props.pagesCount === 0) {
        return null;
    }

    if(props.pagesCount === 1 && !props.hasMorePages) {
        return null;
    }

    const pagesIndexes = Array.from({ length: props.pagesCount }, (_, i) => i);

    const renderOnePageNumber = (pageIndex: number) => {
        return (
            <PageNumberBox key={pageIndex} $isCurrentPage={pageIndex === props.currentPageIndex} onClick={() => props.onPageClick(pageIndex)}>
                {pageIndex + 1}
            </PageNumberBox>
        )
    }

    const RenderNextPageButton = () => {
        if(!props.hasMorePages) {
            return null;
        }
        return (
            <NextPageButtonBox onClick={props.onNextPageClick}>
                <span>
                    {services.language.translate('Next page')}
                </span>
                <ChevronForwardIcon size={0.7}/>
            </NextPageButtonBox>
        )
    }

    return (
        <PageNavigationBox>
            {pagesIndexes.map(renderOnePageNumber)}
            {RenderNextPageButton()}
        </PageNavigationBox>
    )
})

export const BookingSearchResultsComponent = observer(() => {
    const services = useServices();

    const currentStrategy = services.agencyBookingSearch.currentStrategy;

    let results:IBookingSearchResultRecordViewModel[] = [];

    if(currentStrategy.pages.length > 0) {
        results = currentStrategy.pages[currentStrategy.currentPageIndex].records;
    }

    const onPageClick = (pageIndex: number) => {
        currentStrategy.currentPageIndex = pageIndex;
    }

    const onNextPageClick = async () => {
        await currentStrategy.nextPage();
    }

    const renderResults = () => {
        if(currentStrategy.isSearchInProgress) {
            return (
                <NoResultsBox>
                    <SpinnerComponent/>
                </NoResultsBox>
            )
        }
        if(results.length > 0) {
            if(services.agencyBookingSearch.resultViewMode === BookingSearchResultViewMode.CardsView) {
                return (
                    <ResultsBox>
                        <BookingSearchResultsCardsViewComponent results={results} currentStrategy={currentStrategy}/>
                    </ResultsBox>

                )
            } else {
                return (
                    <ResultsBox>
                        <BookingSearchResultsTableViewComponent results={results} currentStrategy={currentStrategy}/>
                    </ResultsBox>

                )
            }

        }

        return (
            <NoResultsBox>
                {services.language.translate('There are no results to show')}
            </NoResultsBox>
        )
    }

    return (
        <ContainerBox>
            <BookingSearchResultsHeaderComponent/>
            {renderResults()}
            <PageNavigationComponent currentPageIndex={currentStrategy.currentPageIndex}
                                    pagesCount={currentStrategy.pages.length}
                                    hasMorePages={currentStrategy.hasMorePages}
                                    onPageClick={onPageClick}
                                    onNextPageClick={onNextPageClick}
                                    />
        </ContainerBox>
    )
})